import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./orderMobileRowPopup.css";

/**
 * Popup/sheet mode when list or table exceeds this count (i.e. 7+ fields).
 * See `usePopup`: 7+ desktop columns OR 7+ mobile KV rows → card shows first N preview rows; tap opens sheet with full rows.
 */
export const ORDER_MOBILE_NO_POPUP_MAX_COLS = 6;

/** Key-value rows visible on the mobile card when sheet mode is on; rest only in the popup. */
export const ORDER_MOBILE_PREVIEW_ROW_COUNT = 7;

const PRIMARY_LABELS = new Set([
  "pairs",
  "pair",
  "contract",
  "currency",
  "coin",
  "project",
  "swapping currencies",
  "lead trader",
]);

const TYPE_LABELS = new Set(["type", "order type"]);

function isActionsNode(child) {
  if (!React.isValidElement(child)) return false;
  const cn = child.props?.className;
  return typeof cn === "string" && cn.includes("so-mobileCard__actions");
}

function partitionMobileCardChildren(children) {
  const all = React.Children.toArray(children).filter(Boolean);
  let actions = null;
  const rows = [];
  for (const c of all) {
    if (isActionsNode(c)) actions = c;
    else rows.push(c);
  }
  return { rows, actions };
}

/** Read the first <span> text in a so-mobileCard__row (the field label). */
function getMobileRowLabel(rowEl) {
  if (!React.isValidElement(rowEl)) return "";
  const ch = rowEl.props?.children;
  const arr = React.Children.toArray(ch);
  const first = arr[0];
  if (React.isValidElement(first)) {
    const t = first.props?.children;
    if (typeof t === "string") return t.trim();
  }
  return "";
}

function collectPlainText(node) {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(collectPlainText).join("");
  if (React.isValidElement(node)) {
    if (node.props?.children != null) return collectPlainText(node.props.children);
    return "";
  }
  return "";
}

/** Plain text from value column(s) (all spans after the label). */
function rowValuePlainText(rowEl) {
  if (!React.isValidElement(rowEl)) return "";
  const parts = React.Children.toArray(rowEl.props.children).slice(1);
  const text = parts.map(collectPlainText).join(" ").replace(/\s+/g, " ").trim();
  return text;
}

/** Split `so-contractCell__symbol` / `so-contractCell__time` stacks (e.g. Futures trade Contract cell). */
function extractContractStackParts(rowEl) {
  let symbol = "";
  let time = "";
  if (!React.isValidElement(rowEl)) return { symbol, time };

  const scanNode = (node) => {
    if (!React.isValidElement(node)) return;
    const cn = String(node.props?.className || "");
    if (cn.includes("so-contractCell__symbol")) {
      symbol = collectPlainText(node.props.children).trim();
    } else if (cn.includes("so-contractCell__time")) {
      time = collectPlainText(node.props.children).trim();
    }
    const kids = React.Children.toArray(node.props?.children);
    kids.forEach(scanNode);
  };

  React.Children.toArray(rowEl.props.children)
    .slice(1)
    .forEach(scanNode);

  return { symbol, time };
}

/** Keep "Status" field rows at the end of the list (before action buttons). */
export function orderRowsStatusLast(rows) {
  if (!rows.length) return rows;
  const non = [];
  const statusRows = [];
  for (const r of rows) {
    if (getMobileRowLabel(r).toLowerCase() === "status") statusRows.push(r);
    else non.push(r);
  }
  return [...non, ...statusRows];
}

function peelHeroFromRows(rows, peelOpts = {}) {
  const { keepTypeInList = false } = peelOpts;
  const skip = new Set();
  let primaryIdx = -1;
  rows.forEach((r, i) => {
    const lab = getMobileRowLabel(r).toLowerCase();
    if (primaryIdx < 0 && PRIMARY_LABELS.has(lab)) primaryIdx = i;
  });
  let primaryText = null;
  let datetimeLine = null;
  if (primaryIdx >= 0) {
    const primaryRow = rows[primaryIdx];
    const { symbol, time } = extractContractStackParts(primaryRow);
    if (symbol || time) {
      primaryText = symbol || rowValuePlainText(primaryRow) || null;
      datetimeLine = time || null;
    } else {
      primaryText = rowValuePlainText(primaryRow) || null;
    }
    skip.add(primaryIdx);
  }

  let sideIdx = -1;
  let typeIdx = -1;
  rows.forEach((r, i) => {
    if (skip.has(i)) return;
    const lab = getMobileRowLabel(r).toLowerCase();
    if (lab === "side") sideIdx = i;
    if (!keepTypeInList && TYPE_LABELS.has(lab) && typeIdx < 0) typeIdx = i;
  });

  let secondaryEl = null;
  const sideTxt = sideIdx >= 0 ? rowValuePlainText(rows[sideIdx]) : "";
  const typTxt = typeIdx >= 0 ? rowValuePlainText(rows[typeIdx]) : "";
  if (sideIdx >= 0) skip.add(sideIdx);
  if (typeIdx >= 0) skip.add(typeIdx);

  if (sideTxt || typTxt) {
    const line = [sideTxt, typTxt].filter(Boolean).join(" ").trim();
    // Tone from Side when present (spot); else Type/Fiat "Buy" | "Sell" only in typTxt
    let isBuy = /buy/i.test(sideTxt);
    let isSell = /sell/i.test(sideTxt);
    if (!isBuy && !isSell) {
      isBuy = /buy/i.test(typTxt);
      isSell = /sell/i.test(typTxt);
    }
    const tone = isBuy ? "orders-mobCard__subtype--buy" : isSell ? "orders-mobCard__subtype--sell" : "";
    secondaryEl = (
      <span className={`orders-mobCard__subtype ${tone}`.trim()}>{line || "—"}</span>
    );
  }

  const bodyRows = rows.filter((_, i) => !skip.has(i));
  return { primaryText, secondaryEl, datetimeLine, bodyRows };
}

function MobileOrderHero({ primary, secondary, datetime }) {
  const hasSecondary = Boolean(secondary);
  const hasPrimary =
    primary != null &&
    primary !== false &&
    !(typeof primary === "string" && !primary.trim());
  const hasDatetime =
    datetime != null &&
    datetime !== false &&
    !(typeof datetime === "string" && !String(datetime).trim());

  if (!hasPrimary && !hasSecondary && !hasDatetime) return null;

  const showChevron = typeof primary === "string" || typeof primary === "number";

  return (
    <div className="orders-mobCard__hero">
      {hasPrimary ? (
        <div className="orders-mobCard__pairLine">
          <span className="orders-mobCard__pair">{primary}</span>
          {showChevron ? <i className="ri-arrow-right-s-line orders-mobCard__chev" aria-hidden /> : null}
        </div>
      ) : null}
      {hasDatetime ? <div className="orders-mobCard__datetime">{datetime}</div> : null}
      {hasSecondary ? <div className="orders-mobCard__subtypeRow">{secondary}</div> : null}
    </div>
  );
}

/**
 * Mobile sheet when EITHER:
 * - desktop table has more than 6 columns (7+), e.g. Open Orders with pair/side only in hero → 6 KV rows, or
 * - mobile list has more than 6 KV rows (`fullSortedRows` before peel), e.g. Trade History.
 * Then: tap hero + preview opens full sheet with every row (card lists at most `ORDER_MOBILE_PREVIEW_ROW_COUNT` rows).
 * If both ≤6 → no sheet (same format on all order sidebar pages).
 *
 * Optional `heroPrimary` / `heroSecondary` / `heroDatetime` override auto-detect (pair in `orders-mobCard__pair`,
 * date/time line below when `so-contractCell__time` exists or `heroDatetime` is set).
 *
 * `skipMobileHero`: no top hero strip; all rows stay in the KV list (e.g. deposit/withdraw/transaction history).
 *
 * `keepTypeInList`: do not peel "Type" / "Order type" into the hero subtitle (e.g. Crypto/Fiat stays in the list; Coin can still be hero).
 *
 * `externalDetail`: when true, skip the built-in mobile sheet and make the card open the parent’s detail modal (via `onOpenExternalDetail`).
 *
 * `includeActionsInPopup`: when false, `so-mobileCard__actions` stay on the card and are not repeated inside the detail sheet.
 */
export default function OrderMobileCardShell({
  desktopColumnCount,
  popupTitle = "Details",
  heroPrimary,
  heroSecondary,
  heroDatetime,
  skipMobileHero = false,
  keepTypeInList = false,
  externalDetail = false,
  onOpenExternalDetail,
  includeActionsInPopup = true,
  children,
}) {
  const { rows: rawRows, actions } = partitionMobileCardChildren(children);
  const fullSortedRows = orderRowsStatusLast(rawRows);

  const explicitHero = heroPrimary != null || heroSecondary != null;
  let resolvedPrimary = explicitHero ? heroPrimary : null;
  let resolvedSecondary = explicitHero ? heroSecondary : null;
  let resolvedDatetime = explicitHero ? heroDatetime ?? null : null;
  let bodyRows = fullSortedRows;

  if (skipMobileHero) {
    resolvedPrimary = null;
    resolvedSecondary = null;
    resolvedDatetime = null;
    bodyRows = fullSortedRows;
  } else if (!explicitHero) {
    const peeled = peelHeroFromRows(fullSortedRows, { keepTypeInList });
    if (peeled.primaryText) resolvedPrimary = peeled.primaryText;
    if (peeled.secondaryEl) resolvedSecondary = peeled.secondaryEl;
    if (peeled.datetimeLine) resolvedDatetime = peeled.datetimeLine;
    bodyRows = peeled.bodyRows;
  }

  const wouldPopup =
    desktopColumnCount > ORDER_MOBILE_NO_POPUP_MAX_COLS ||
    fullSortedRows.length > ORDER_MOBILE_NO_POPUP_MAX_COLS;
  const usePopup = !externalDetail && wouldPopup;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const shellClass = `so-mobileCard orders-mobCard${usePopup ? " orders-mrp-card" : ""}`;

  const openExternalIfAllowed = (e) => {
    if (!externalDetail || !onOpenExternalDetail) return;
    const t = e?.target;
    if (t instanceof Element && t.closest("button, a[href], input, textarea, select")) return;
    onOpenExternalDetail();
  };

  const renderHero = () => (
    <MobileOrderHero primary={resolvedPrimary} secondary={resolvedSecondary} datetime={resolvedDatetime} />
  );

  const close = () => setOpen(false);

  const modalBodyRows = bodyRows;

  const modal =
    open &&
    usePopup &&
    createPortal(
      <div className="orders-mrp" role="dialog" aria-modal="true" aria-labelledby="orders-mrp-heading">
        <button type="button" className="orders-mrp__backdrop" aria-label="Close" onClick={close} />
        <div className="orders-mrp__panel orders-mrp__panel--light">
          <div className="orders-mrp__head">
            <span id="orders-mrp-heading" className="orders-mrp__title">
              {popupTitle}
            </span>
            <button type="button" className="orders-mrp__close" aria-label="Close" onClick={close} />
          </div>
          <div className="orders-mrp__body">
            <div className="orders-mrp__detailInner">
              {renderHero()}
              <div className="orders-mobCard__body orders-mobCard__body--plain">{modalBodyRows}</div>
            </div>
            {includeActionsInPopup && actions ? <div className="orders-mrp__actionsSlot">{actions}</div> : null}
          </div>
          <div className="orders-mrp__footer">
            <button type="button" className="orders-mrp__ok" onClick={close}>
              OK
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  if (!usePopup) {
    return (
      <div
        className={shellClass}
        role={externalDetail ? "button" : undefined}
        tabIndex={externalDetail ? 0 : undefined}
        onClick={externalDetail ? openExternalIfAllowed : undefined}
        onKeyDown={
          externalDetail
            ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenExternalDetail?.();
              }
            }
            : undefined
        }
        style={externalDetail ? { cursor: "pointer" } : undefined}
        aria-label={externalDetail ? `Open ${popupTitle}` : undefined}
      >
        {renderHero()}
        <div className="orders-mobCard__body orders-mobCard__body--plain">{bodyRows}</div>
        {actions}
      </div>
    );
  }

  const previewRows = bodyRows.slice(0, ORDER_MOBILE_PREVIEW_ROW_COUNT);

  const openSheet = () => setOpen(true);

  return (
    <>
      <div className={shellClass}>
        <div
          className="orders-mobCard__tapZone"
          role="button"
          tabIndex={0}
          onClick={openSheet}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openSheet();
            }
          }}
          aria-label={`Open ${popupTitle}`}
        >
          {renderHero()}
          <div className="orders-mobCard__body orders-mobCard__body--plain">{previewRows}</div>
        </div>
        {actions}
      </div>
      {modal}
    </>
  );
}
